import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Search,
  MoreHorizontal,
  Trash2,
  Pause,
  Play,
  ExternalLink,
  Pencil,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { useConfirm } from "@/components/admin/ConfirmDialog";
import { DateCell } from "@/components/admin/DateCell";
import { StatusBadges } from "@/components/admin/StatusBadge";
import { AdminTooltip } from "@/components/admin/AdminTooltip";
import { AdHoverPreview } from "@/components/admin/AdHoverPreview";
import { AdThumb } from "@/components/admin/AdThumb";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CATEGORIES, categoryLabel } from "@/lib/categories";
import { listAdminAds, adminDeleteAd, adminSetAdPaused } from "@/lib/admin.functions";

type Filter =
  | "all"
  | "paused"
  | "sold"
  | "active"
  | "expired"
  | "promoted"
  | "hidden";
type SortKey = "created_at" | "updated_at" | "expires_at" | "title" | "price" | "category" | "location";

const FILTER_KEYS: Filter[] = [
  "all",
  "active",
  "paused",
  "sold",
  "expired",
  "promoted",
  "hidden",
];
const SORT_KEYS: SortKey[] = [
  "created_at",
  "updated_at",
  "expires_at",
  "title",
  "price",
  "category",
  "location",
];

type AdsSearch = {
  filter?: Filter;
  sort?: SortKey;
  dir?: "asc" | "desc";
  page?: number;
  category?: string;
  q?: string;
  seller?: "private" | "business" | "";
  owner?: string;
};

export const Route = createFileRoute("/_authenticated/admin/annonser")({
  validateSearch: (search: Record<string, unknown>): AdsSearch => ({
    filter: FILTER_KEYS.includes(String(search["filter"]) as Filter)
      ? (search["filter"] as Filter)
      : "all",
    sort: SORT_KEYS.includes(String(search["sort"]) as SortKey)
      ? (search["sort"] as SortKey)
      : "created_at",
    dir: search["dir"] === "asc" ? "asc" : "desc",
    page: Number.isFinite(Number(search["page"])) ? Math.max(0, Number(search["page"]) || 0) : 0,
    category: typeof search["category"] === "string" ? (search["category"] as string) : "",
    q: typeof search["q"] === "string" ? (search["q"] as string) : "",
    seller: search["seller"] === "private" || search["seller"] === "business" ? search["seller"] : "",
    owner: typeof search["owner"] === "string" ? (search["owner"] as string) : "",
  }),

  head: () => ({
    meta: [
      { title: "Alla annonser — Administration | Sellavo" },
      {
        name: "description",
        content: "Lista, sortera, granska, ändra och ta bort annonser i Sellavos administration.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Annonser">
      <AdsBody />
    </AdminShell>
  ),
});

const FILTERS: { key: Filter; label: string; tooltip: string }[] = [
  { key: "all", label: "Alla", tooltip: "Visa alla annonser oavsett status." },
  { key: "active", label: "Aktiva", tooltip: "Annonser som är publicerade och synliga." },
  { key: "paused", label: "Pausade", tooltip: "Annonser som säljaren har pausat tillfälligt." },
  { key: "sold", label: "Sålda", tooltip: "Annonser som är markerade som sålda." },
  { key: "expired", label: "Utgångna", tooltip: "Annonser vars publiceringsperiod har gått ut." },
  { key: "promoted", label: "Lyfta", tooltip: "Annonser som är framhävda just nu." },
  { key: "hidden", label: "Dolda", tooltip: "Dolda annonser som bara superadmin ser." },
];

const PAGE_SIZES = [25, 50, 100, 200];


function AdsBody() {
  const { confirm, dialog } = useConfirm();
  const s = Route.useSearch();
  const filter = s.filter ?? "all";
  const sort = s.sort ?? "created_at";
  const dir = s.dir ?? "desc";
  const page = s.page ?? 0;
  const category = s.category ?? "";
  const q = s.q ?? "";
  const seller = s.seller ?? "";
  const owner = s.owner ?? "";

  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const [input, setInput] = useState(q);
  const [pageSize, setPageSize] = useState(50);

  // Debounce fritextsökningen mot URL:en
  useEffect(() => {
    const t = setTimeout(() => {
      if (input !== q) navigate({ search: (p: any) => ({ ...p, q: input, page: 0 }) });
    }, 350);
    return () => clearTimeout(t);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAds = useServerFn(listAdminAds);
  const removeAd = useServerFn(adminDeleteAd);
  const setPaused = useServerFn(adminSetAdPaused);

  const ads = useQuery({
    queryKey: ["admin-ads-page", q, filter, category, sort, dir, page, pageSize, seller, owner],
    queryFn: () =>
      fetchAds({
        data: {
          search: q || undefined,
          filter,
          category: category || undefined,
          seller: seller || undefined,
          userId: owner || undefined,
          sort,
          dir,
          page,
          limit: pageSize,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-ads-page"] });
    qc.invalidateQueries({ queryKey: ["admin-ads"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const del = useMutation({
    mutationFn: (slug: string) => removeAd({ data: { slug } }),
    onSuccess: invalidate,
  });
  const pause = useMutation({
    mutationFn: (vars: { slug: string; paused: boolean }) => setPaused({ data: vars }),
    onSuccess: invalidate,
  });

  const rows = ads.data?.rows ?? [];
  const total = ads.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (key: SortKey) =>
    navigate({
      search: (p: any) => ({
        ...p,
        sort: key,
        dir: p.sort === key && p.dir === "desc" ? "asc" : "desc",
        page: 0,
      }),
    });

  const RowActions = ({ ad }: { ad: any }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Åtgärder">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem asChild>
          <Link to="/admin/annons/$slug" params={{ slug: ad.slug }}>
            <Pencil className="mr-2 h-4 w-4" /> Öppna i admin
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/a/$slug" params={{ slug: ad.slug }}>
            <ExternalLink className="mr-2 h-4 w-4" /> Visa annons
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={pause.isPending}
          onSelect={() => pause.mutate({ slug: ad.slug, paused: !ad.is_paused })}
        >
          {ad.is_paused ? (
            <>
              <Play className="mr-2 h-4 w-4" /> Återaktivera
            </>
          ) : (
            <>
              <Pause className="mr-2 h-4 w-4" /> Pausa
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={del.isPending}
          className="text-destructive focus:text-destructive"
          onSelect={async () => {
            const ok = await confirm({
              title: "Ta bort annonsen?",
              description: `”${ad.title}” tas bort permanent. Det går inte att ångra.`,
              confirmLabel: "Ta bort annonsen",
            });
            if (ok) del.mutate(ad.slug);
          }}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Ta bort
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const Th = ({
    label,
    sortKey,
    width,
  }: {
    label: string;
    sortKey?: SortKey;
    width?: string;
  }) => (
    <th className={`whitespace-nowrap px-3 py-2 ${width ?? ""}`}>
      {sortKey ? (
        <button
          type="button"
          onClick={() => toggleSort(sortKey)}
          className={`inline-flex items-center gap-1 whitespace-nowrap uppercase tracking-wide hover:text-foreground ${
            sort === sortKey ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {label}
          {sort === sortKey ? (
            dir === "asc" ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5" />
            )
          ) : (
            <ArrowUp className="h-3.5 w-3.5 opacity-40" />
          )}
        </button>
      ) : (
        <span className="text-muted-foreground">{label}</span>
      )}
    </th>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <AdminTooltip key={f.key} text={f.tooltip}>
            <Button
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              onClick={() => navigate({ search: (p: any) => ({ ...p, filter: f.key, page: 0 }) })}
            >
              {f.label}
            </Button>
          </AdminTooltip>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Sök rubrik, ort, säljare, e-post eller slug"
            className="h-10 w-72 pl-9"
          />
        </div>

        <Select
          value={category || "all"}
          onValueChange={(v) =>
            navigate({ search: (p: any) => ({ ...p, category: v === "all" ? "" : v, page: 0 }) })
          }
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Alla kategorier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla kategorier</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.slug} value={c.slug}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={seller || "all"}
          onValueChange={(v) =>
            navigate({ search: (p: any) => ({ ...p, seller: v === "all" ? "" : v, owner: "", page: 0 }) })
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Alla säljare" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla säljare</SelectItem>
            <SelectItem value="private">Privat</SelectItem>
            <SelectItem value="business">Företag</SelectItem>
          </SelectContent>
        </Select>
        {owner && (
          <button
            type="button"
            className="text-sm text-primary hover:underline"
            onClick={() => navigate({ search: (p: any) => ({ ...p, owner: "", page: 0 }) })}
          >
            Visa alla företag ×
          </button>
        )}


        <Select
          value={String(pageSize)}
          onValueChange={(v) => {
            setPageSize(Number(v));
            navigate({ search: (p: any) => ({ ...p, page: 0 }) });
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZES.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n} / sida
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(q || category || filter !== "all") && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setInput("");
              navigate({
                search: () => ({
                  filter: "all" as Filter,
                  sort,
                  dir,
                  page: 0,
                  category: "",
                  q: "",
                }),
              });
            }}
          >
            Rensa filter
          </Button>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {ads.isLoading
          ? "Laddar…"
          : `${total.toLocaleString("sv-SE")} annonser · sida ${page + 1} av ${pages}`}
      </p>

      <div className="hidden overflow-x-auto rounded-lg border border-border bg-card lg:block">
        <table className="w-full min-w-[1120px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <Th label="Rubrik" sortKey="title" />
              <Th label="Kategori" sortKey="category" />
              <Th label="Ort" sortKey="location" />
              <Th label="Pris" sortKey="price" />
              <Th label="Status" />
              <Th label="Skapad" sortKey="created_at" />
              <Th label="Går ut" sortKey="expires_at" />
              <Th label="Användare" />
              <Th label="" />
            </tr>
          </thead>
          <tbody>
            {rows.map((ad: any) => (
              <tr key={ad.slug} className="border-t border-border align-middle hover:bg-muted/40">
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="flex min-w-0 max-w-[320px] items-center gap-2">
                    <AdThumb ad={ad} />
                    <AdHoverPreview ad={ad}>
                      <Link
                        to="/admin/annons/$slug"
                        params={{ slug: ad.slug }}
                        title={ad.title}
                        className="block min-w-0 max-w-[260px] truncate cursor-help font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {ad.title}
                      </Link>
                    </AdHoverPreview>
                  </div>

                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {categoryLabel(ad.category ?? "ovrigt")}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{ad.location}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  {Number(ad.price ?? 0).toLocaleString("sv-SE")} kr
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="flex flex-nowrap items-center gap-1">
                    <StatusBadges ad={ad} />
                  </div>
                </td>
                <td className="px-1.5 py-2 text-muted-foreground">
                  <DateCell value={ad.created_at} compact />
                </td>
                <td className="px-1.5 py-2 text-muted-foreground">
                  <DateCell value={ad.expires_at} compact />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {ad.user_id ? (
                    <div className="flex flex-col">
                      <Link
                        to="/admin/anvandare/$userId"
                        params={{ userId: ad.user_id }}
                        className="font-medium text-foreground hover:underline"
                      >
                        {ad.owner_name || ad.contact_name || "Visa ägare"}
                      </Link>
                      {ad.company_name && (
                        <button
                          type="button"
                          onClick={() => navigate({ search: (p: any) => ({ ...p, seller: "business", owner: ad.user_id, page: 0 }) })}
                          className="w-fit text-xs font-medium text-primary hover:underline"
                        >
                          {ad.company_name}
                        </button>
                      )}
                      {ad.contact_email && (
                        <span className="max-w-[180px] truncate text-xs" title={ad.contact_email}>
                          {ad.contact_email}
                        </span>

                      )}
                      <button
                        type="button"
                        className="mt-0.5 text-left text-xs text-primary hover:underline"
                        onClick={() =>
                          navigate({
                            search: (p: any) => ({ ...p, q: ad.contact_email ?? "", page: 0 }),
                          })
                        }
                      >
                        Visa alla annonser
                      </button>
                    </div>
                  ) : (
                    (ad.contact_name ?? "—")
                  )}
                </td>
                <td className="whitespace-nowrap px-2 py-2 text-right">
                  <RowActions ad={ad} />
                </td>
              </tr>
            ))}
            {!ads.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">
                  Inga annonser matchade.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobil och surfplatta: kort i stället för tabell */}
      <div className="space-y-3 lg:hidden">
        {rows.map((ad: any) => (
          <article
            key={ad.slug}
            className="rounded-lg border border-border bg-card p-3 shadow-sm"
          >
            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3">
              <AdThumb ad={ad} size={56} />
              <div className="min-w-0">
                <Link
                  to="/admin/annons/$slug"
                  params={{ slug: ad.slug }}
                  className="line-clamp-2 font-medium hover:underline"
                >
                  {ad.title}
                </Link>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {Number(ad.price ?? 0).toLocaleString("sv-SE")} kr ·{" "}
                  {categoryLabel(ad.category ?? "ovrigt")}
                </p>
                <p className="text-xs text-muted-foreground">{ad.location}</p>
              </div>
              <RowActions ad={ad} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <StatusBadges ad={ad} />
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
              <div className="flex gap-1">
                <dt>Skapad:</dt>
                <dd className="text-foreground/80">
                  <DateCell value={ad.created_at} compact />
                </dd>
              </div>
              <div className="flex gap-1">
                <dt>Går ut:</dt>
                <dd className="text-foreground/80">
                  <DateCell value={ad.expires_at} compact />
                </dd>
              </div>
              <div className="col-span-2 min-w-0 truncate">
                {ad.user_id ? (
                  <Link
                    to="/admin/anvandare/$userId"
                    params={{ userId: ad.user_id }}
                    className="text-primary hover:underline"
                  >
                    {ad.owner_name || ad.contact_name || "Visa ägare"}
                    {ad.contact_email ? ` · ${ad.contact_email}` : ""}
                  </Link>
                ) : (
                  (ad.contact_name ?? "—")
                )}
              </div>
            </dl>
          </article>
        ))}
        {!ads.isLoading && rows.length === 0 && (
          <p className="rounded-lg border border-border bg-card px-3 py-8 text-center text-muted-foreground">
            Inga annonser matchade.
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">
          Visar {rows.length ? page * pageSize + 1 : 0}–{page * pageSize + rows.length} av{" "}
          {total.toLocaleString("sv-SE")}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={page === 0 || ads.isFetching}
            onClick={() => navigate({ search: (p: any) => ({ ...p, page: p.page - 1 }) })}
          >
            <ChevronLeft className="h-4 w-4" /> Föregående
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={page + 1 >= pages || ads.isFetching}
            onClick={() => navigate({ search: (p: any) => ({ ...p, page: p.page + 1 }) })}
          >
            Nästa <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {(del.error instanceof Error || pause.error instanceof Error) && (
        <p className="text-sm text-destructive">
          {(del.error as Error)?.message ?? (pause.error as Error)?.message}
        </p>
      )}
      {dialog}
    </div>
  );
}
